import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from '@cyphex/ui';
import type { UserPreferences } from '@cyphex/types';
import { chatApi } from '../api';
import { CHAT_KEYS, DEFAULT_QUICK_REACTIONS, MAX_QUICK_REACTIONS } from '../constants';

interface UseQuickReactionsResult {
  /** Always exactly MAX_QUICK_REACTIONS entries — falls back to the defaults. */
  emoji: string[];
  setEmoji: (next: string[]) => void;
  /** Replace one slot; the affordance the picker in the hover bar uses. */
  replaceAt: (index: number, value: string) => void;
}

/**
 * The three favourite emoji offered inline on hover.
 *
 * Stored in `/me/preferences`, which is MERGED not replaced server-side, so two
 * devices editing different preferences cannot clobber each other.
 */
export function useQuickReactions(): UseQuickReactionsResult {
  const client = useQueryClient();

  const { data } = useQuery<UserPreferences>({
    queryKey: CHAT_KEYS.preferences(),
    queryFn: () => chatApi.getPreferences(),
    staleTime: 5 * 60_000,
  });

  const stored = data?.quickReactions ?? [];
  const emoji = Array.from(
    { length: MAX_QUICK_REACTIONS },
    (_unused, index) => stored[index] ?? DEFAULT_QUICK_REACTIONS[index] ?? '👍',
  );

  const mutation = useMutation({
    mutationFn: (quickReactions: string[]) => chatApi.updatePreferences({ quickReactions }),
    onSuccess: (preferences) => client.setQueryData(CHAT_KEYS.preferences(), preferences),
    onError: () => toast.error('Could not save your quick reactions.'),
  });

  const setEmoji = useCallback(
    (next: string[]) => mutation.mutate(next.slice(0, MAX_QUICK_REACTIONS)),
    [mutation],
  );

  const replaceAt = useCallback(
    (index: number, value: string) => {
      const next = [...emoji];
      next[index] = value;
      mutation.mutate(next.slice(0, MAX_QUICK_REACTIONS));
    },
    [emoji, mutation],
  );

  return { emoji, setEmoji, replaceAt };
}
